import {
	resolveTableViewState,
	Table,
	TableCard,
	TableStateRow,
	type TableStateRowProps,
	type TableVariant,
	type TableViewState,
} from "@components";
import { ChevronDown } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { cx } from "@/utils/cx";
import type {
	ListingDataTableColumn,
	ListingDataTableProps,
} from "./listing-data-table-types";

const headerRowClass =
	"h-11 border-0 bg-[#F5F5F5] [&>tr>th]:after:bg-border-secondary";

const headerLabelClass =
	"tracking-wide font-medium text-[#717680] text-[12px] leading-[18px]";

const listingHeaderLabelClass =
	"whitespace-nowrap font-semibold text-primary text-xs";

function alignClass(align: ListingDataTableColumn<object>["align"]): string {
	if (align === "right") {
		return "text-right";
	}
	if (align === "center") {
		return "text-center";
	}
	return "text-left";
}
function getPresentationClassNames(
	variant: TableVariant,
	density: "default" | "compact"
) {
	const isListing = variant === "listing";
	return {
		isListing,
		densityClassName:
			isListing && density === "compact"
				? "px-4 first:pl-4 last:pr-4"
				: undefined,
		headerClassName: isListing ? undefined : headerRowClass,
		headCellClassName: isListing ? undefined : "px-4 py-2",
		labelClassName: isListing ? listingHeaderLabelClass : headerLabelClass,
	};
}

type TableStateOptions = Omit<TableStateRowProps, "state">;

function withTableState(
	rows: ReactNode[],
	viewState: TableViewState,
	options: TableStateOptions
): ReactNode {
	if (viewState.view !== "data") {
		return <TableStateRow {...options} state={viewState.view} />;
	}
	if (viewState.hasRefreshError) {
		return [
			<TableStateRow {...options} key="refresh-error" state="refresh-error" />,
			...rows,
		];
	}
	return rows;
}

function mainRowClassName({
	isExpanded,
	isListing,
	isRowInteractive,
}: {
	isExpanded: boolean;
	isListing: boolean;
	isRowInteractive: boolean;
}): string {
	return cx(
		isExpanded && "border-none bg-[#F5F5F5]",
		!(isExpanded || isListing) && "border-0",
		isRowInteractive && "cursor-pointer"
	);
}

export function ListingDataTable<T extends object>({
	ariaLabel,
	columns,
	items,
	getRowKey,
	renderCell,
	sortDescriptor,
	onSortChange,
	fixedLayout = false,
	cardHeader,
	headerActions,
	stackHeader = false,
	expandable = false,
	renderExpandedRow,
	onRowAction,
	variant = "default",
	density = "default",
	isLoading = false,
	isError = false,
	onRetry,
	loadingLabel,
	emptyContent,
	errorMessage,
	errorContent,
}: ListingDataTableProps<T>) {
	const {
		isListing,
		densityClassName,
		headerClassName,
		headCellClassName,
		labelClassName,
	} = getPresentationClassNames(variant, density);
	const viewState = resolveTableViewState({
		itemCount: items.length,
		isLoading,
		isError,
	});
	const [expandedKey, setExpandedKey] = useState<unknown | null>(null);

	const defaultRenderCell = (item: T, col: ListingDataTableColumn<T>) => {
		const value = item[col.id as keyof T];
		if (value === null || value === undefined) {
			return "";
		}
		if (typeof value === "string") {
			return value.trim() || "";
		}
		if (typeof value === "number") {
			return String(value);
		}
		return String(value);
	};

	const render = renderCell ?? defaultRenderCell;

	const sortEnabled = onSortChange !== undefined;

	const handleToggleExpand = (item: T, index: number) => {
		if (expandable !== true) {
			return;
		}
		const key = getRowKey(item, index);
		setExpandedKey((prev: unknown) => (prev === key ? null : key));
	};

	const handleRowActivate = (item: T, index: number) => {
		if (expandable === true) {
			handleToggleExpand(item, index);
			return;
		}
		if (onRowAction) {
			onRowAction(item, index);
		}
	};

	const rows = items.flatMap((item, index) => {
		const key = getRowKey(item, index);
		const isExpanded = expandable && expandedKey === key;
		const isRowInteractive = expandable === true || onRowAction !== undefined;

		let rowActivateHandler: (() => void) | undefined;
		if (isRowInteractive) {
			rowActivateHandler = () => {
				handleRowActivate(item, index);
			};
		}

		const mainRow = (
			<Table.Row
				className={mainRowClassName({
					isExpanded,
					isListing,
					isRowInteractive,
				})}
				columns={columns}
				highlightSelectedRow={false}
				id={String(key)}
				key={String(key)}
				onAction={rowActivateHandler}
			>
				{columns.map((col, colIndex) => (
					<Table.Cell
						className={cx(
							alignClass(col.align),
							densityClassName,
							col.cellClassName,
							"text-primary"
						)}
						key={col.id}
					>
						<div
							className={cx(
								"flex items-center gap-2",
								col.align === "right" && "justify-end",
								col.align === "center" && "justify-center"
							)}
						>
							<div className="min-w-0 flex-1">
								{render(item, col, colIndex)}
							</div>
						</div>
					</Table.Cell>
				))}
			</Table.Row>
		);

		if (!(expandable && renderExpandedRow)) {
			return [mainRow];
		}

		if (!isExpanded) {
			return [mainRow];
		}

		const detailRow = (
			<Table.Row
				className="h-auto hover:bg-transparent"
				columns={[{ id: "detail", label: "" }]}
				highlightSelectedRow={false}
				id={`${String(key)}-detail`}
				key={`${String(key)}-detail`}
			>
				<Table.Cell className="bg-[#F5F5F5] px-6 py-0" colSpan={columns.length}>
					{renderExpandedRow(item)}
					<div className="flex justify-center py-2">
						<button
							aria-label="Recolher detalhes da linha"
							className="rounded-md p-1 text-fg-quaternary transition-colors"
							onClick={() => {
								setExpandedKey(null);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									setExpandedKey(null);
								}
							}}
							tabIndex={0}
							type="button"
						>
							<ChevronDown
								aria-hidden
								className="size-4 rotate-180 stroke-[2.5px]"
							/>
						</button>
					</div>
				</Table.Cell>
			</Table.Row>
		);

		return [mainRow, detailRow];
	});

	let headerBlock: ReactNode = null;

	if (cardHeader !== undefined && stackHeader === true) {
		headerBlock = (
			<div
				className={cx(
					"border-secondary border-b bg-primary px-4 py-5 md:px-6",
					"flex flex-col items-start gap-4"
				)}
			>
				<div className="flex w-full flex-col gap-0.5">
					<h2 className="font-semibold text-lg text-primary">
						{cardHeader.title}
					</h2>
					{cardHeader.description !== undefined &&
					cardHeader.description !== "" ? (
						<p className="text-sm text-tertiary">{cardHeader.description}</p>
					) : null}
				</div>
				{headerActions !== undefined ? headerActions : null}
			</div>
		);
	} else if (cardHeader !== undefined) {
		headerBlock = (
			<TableCard.Header
				contentTrailing={cardHeader.trailing}
				description={cardHeader.description}
				title={cardHeader.title}
			/>
		);
	}

	const tableProps =
		sortEnabled === true
			? {
					sortDescriptor,
					onSortChange,
				}
			: {};

	const tableClassName = fixedLayout === true ? "table-fixed" : "";

	const totalMinWidth = columns.reduce(
		(sum, col) => sum + (col.minWidth ?? 0),
		0
	);

	const getColumnStyle = (col: ListingDataTableColumn<T>) => {
		if (!col.minWidth) {
			return {};
		}
		if (fixedLayout === true) {
			return {
				minWidth: `${col.minWidth}px`,
				width: `${(col.minWidth / totalMinWidth) * 100}%`,
			};
		}
		return { minWidth: `${col.minWidth}px` };
	};

	const bodyRows = withTableState(rows, viewState, {
		colSpan: columns.length,
		onRetry,
		loadingLabel,
		emptyContent,
		errorMessage,
		errorContent,
	});

	return (
		<>
			{headerBlock}
			<Table
				aria-label={ariaLabel}
				className={tableClassName}
				isRefreshing={viewState.isRefreshing}
				selectionMode="none"
				variant={variant}
				{...tableProps}
			>
				<Table.Header bordered className={headerClassName} columns={columns}>
					{(column) => {
						const col = column as ListingDataTableColumn<T>;
						const sortable = col.sortable === true && sortEnabled === true;
						return (
							<Table.Head
								allowsSorting={sortable}
								className={cx(
									headCellClassName,
									densityClassName,
									col.headerClassName
								)}
								id={col.id}
								isRowHeader={columns.indexOf(col) === 0}
								key={col.id}
								style={getColumnStyle(col)}
							>
								<span className={labelClassName}>{col.label}</span>
							</Table.Head>
						);
					}}
				</Table.Header>

				<Table.Body>{bodyRows}</Table.Body>
			</Table>
		</>
	);
}
